import { Button, Container, Grid, Link, TextField, Typography, useMediaQuery } from '@material-ui/core';
import { AxiosError } from 'axios';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Footer from '../../../components/Footer';
import envConfig from '../../../config/environment-config';
import { RoutesEnum } from '../../../constants';
import { useAlert } from '../../../contexts/pharmaceutical-portal/Alert.context';
import { useLoader } from '../../../contexts/pharmaceutical-portal/Loader.context';
import loginPharmaceuticalService from '../../../services/loginPharmaceuticalService';
import { verifyRecaptchaService } from '../../../services/reCAPTCHAService';
import { SelectInput } from '../common/components';
import { IPharmaceuticalCrendentials, IUseFormData, LocalStorageKeysEnum } from '../common/contracts';
import { localStorageMethods } from '../common/helpers';
import { statesList } from '../common/helpers/statesListMock';
import { InputsBox, inputLabelProps, inputMUIDefaultStyles } from '../common/styles/FormStyles';
import { PasswordInput } from '../signup/components/PasswordInput';
import styles from './LoginPage.module.css';

function LoginInputs({ formData, showLogin, recaptchaRef, recaptchaValueSet }: { formData: IUseFormData, showLogin: boolean, recaptchaRef: React.RefObject<ReCAPTCHA>, recaptchaValueSet: (value: string | null) => void }) {
  const display = showLogin ? 'initial' : 'none';
  return (
    <div style={{ display }}>
      <InputsBox className="gap-2 my-4">
        <PasswordInput
          title="Digite sua senha"
          helperText=""
          formData={formData}
          propertieFormName="password"
          className="w-100"
        />
      </InputsBox>
      <InputsBox className="my-2">
        <Link 
          href={`${envConfig.healthPassportPasswordResetUrl}?client=${envConfig.healthPassportPasswordResetClient}&program=${envConfig.healthPassportPasswordResetProgram}`}
          target="_blank"
          className={`${styles.ForgotPassword} btn_login_farmaceutico`}>
          Esqueci minha senha
        </Link>
      </InputsBox>
      <div className={styles.CaptchaContainer}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={envConfig.googleSiteKey}
          onChange={recaptchaValueSet}
        />
      </div>
      <Button
        className={styles.ButtonLogin}
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
      >
        Validar
      </Button>
    </div>
  );
}

const PharmaceuticalLoginPage: React.FC<any> = (): JSX.Element => {
  const { push } = useHistory();
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const { showAlert, hideAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const recaptchaValueRef = useRef('' as any);
  const [isRecaptchaDisabled, setIsRecaptchaDisabled] = useState(false);

  const recaptchaValueSet = async (value: string | null) => {
    recaptchaValueRef.current = value || '';

    if (!value) {
      recaptchaRef.current?.reset();
      setIsRecaptchaDisabled(true);
    }

    try {
      const response = await verifyRecaptchaService.verifyRecaptcha(recaptchaValueRef.current);
      const isRecaptchaValid = response.data;

      if (!isRecaptchaValid) {
        setIsRecaptchaDisabled(true);
      } else {
        setIsRecaptchaDisabled(false);
      }
    } catch (error) {
      setIsRecaptchaDisabled(true);
    }
  };

  const updateDimensions = () => {
    const container = document.getElementById('container');
    const rect = container?.getBoundingClientRect();
    
    if (rect) {
      const dimensions = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
      }
      return dimensions;
    }
    return { top: 0, left: 0, width: 0, height: 0, bottom: 0 };
  };

  const alertLoginStyles = () => {
    const dimensions = updateDimensions();
    return {
      top: `${dimensions.height + 115}px`,
      left: `${dimensions.left + dimensions.width / 2}px`,
      transform: "translate(-50%, -50%)",
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    unregister,
    getValues,
    setError,
  } = useForm();

  const formData: IUseFormData = {
    register,
    handleSubmit,
    errors,
    control,
    setValue,
    watch,
    unregister,
    getValues,
    setError,
  };

  const doSubmit = async (e: any): Promise<void> => {
    e.preventDefault();
    if (e.key === 'Enter') return;
    const loginValue = watch('login');
    const passwordValue = watch('password');
    
    if (!loginValue || !passwordValue) {
      showAlert('Login incorreto', 'Preencha os campos.', 'error', alertLoginStyles());
      setTimeout(() => hideAlert(), 3000);
      return;
    }

    if (isRecaptchaDisabled || recaptchaValueRef.current === '') {
      showAlert('ReCAPTCHA expirado ou inválido!', 'Por favor, valide o reCAPTCHA antes de continuar.', 'error');
      setTimeout(() => hideAlert(), 3000);
      recaptchaRef.current?.reset();
      recaptchaValueRef.current = '';
      return;
    }

    showLoader();

    try {
      const result = await loginPharmaceuticalService.loginPharmaceutical(loginValue, passwordValue);

      if (!result || !result.data?.access_token) throw new Error('Não foi possível realizar o login');

      localStorageMethods.setItem<IPharmaceuticalCrendentials>(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS, {
        access_token: result?.data?.access_token,
        refresh_token: result?.data?.refresh_token,
        healthPassportId: result?.data?.userinfo?.uuid,
        userId: result?.data?.userinfo?.userId,
        email: result?.data?.userinfo?.email,
        firstName: result?.data?.userinfo?.firstName,
        lastName: result?.data?.userinfo?.lastName,
        fullName: result?.data?.userinfo?.fullName,
        crf: result?.data?.userinfo?.crf,
        crfUf: result?.data?.userinfo?.crfUf,
        avatar: result?.data?.userinfo?.avatar,
        programId: envConfig.boehringerProgramCode,
      });
      window.location.href = '/';
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message;
    
      let alertTitle = 'Usuário Incorreto';
      let alertMessage = 'Tente novamente';
    
      if (errorMessage === 'UserBlocked') {
        alertTitle = 'Usuário bloqueado';
        alertMessage = 'Entre em contato com o suporte pelo telefone 0800 701 6633 ou pelo e-mail abracaravida@funcionalcorp.com.br para desbloquear sua conta.';
      } else if (errorMessage === 'InvalidCredentials') {
        alertTitle = 'Credenciais incorretas';
        alertMessage = 'Confira os dados digitados. Se esqueceu a senha, clique em "Esqueci minha senha" para recuperá-la.';
      }
    
      console.warn(error);
      updateDimensions();
      showAlert(alertTitle, alertMessage, 'error', alertLoginStyles());
      setTimeout(() => hideAlert(), 5000);
      hideLoader();
    }
  };

  const pharmaceuticalNotFound = (login: string) => {
    localStorageMethods.setItem<string>(LocalStorageKeysEnum.PHARMACEUTICAL_CRFUF_LOGIN, login);

    showAlert('Farmacêutico não cadastrado', 'Você será redirecionado para o cadastro.', 'warning', alertLoginStyles());
    setTimeout(() => hideAlert(), 3000);
    setTimeout(() => push(`${RoutesEnum.PHARMACEUTICAL_SIGNUP}`), 3200);
  }

  const userLocated = () => {
    setShowLogin(true);
    showAlert('Usuário localizado', 'Digite sua senha para entrar no portal do farmacêutico.', 'success', alertLoginStyles());
    setTimeout(() => hideAlert(), 3000);
  }

  const verifyPharmaceutical = async (value: string): Promise<void> => {
    showLoader();
    setShowLogin(false);

    try {
      const ret = await loginPharmaceuticalService.verifyPharmaceutical(value);
      switch (ret?.data.status) {
        case '01':
          pharmaceuticalNotFound(value);
          break;
        default:
          userLocated();
          break;
      }
    } catch (error) {
      console.warn(error);
      showAlert('Erro de conexão', 'Ops! Tivemos um erro de conexão. Tente novamente mais tarde.', 'error', alertLoginStyles());
      setTimeout(() => hideAlert(), 3000);
    }
    hideLoader();
  };

  const handleLoginInput = () => {
    const onlyNumberPattern = /^\d+$/;
    const crfValue = watch('crf');
    const ufValue = watch('uf');
    const proceed = (crfValue.length == 5 && (onlyNumberPattern.test(crfValue)))

    if (!proceed) {
      showAlert('Login incorreto', 'CRF é obrigatório e só deve conter números', 'error', alertLoginStyles());
      setTimeout(() => hideAlert(), 3000);
      return;
    }
    
    if(!ufValue || ufValue === '') {
      showAlert('Login incorreto', 'Estado é obrigatório', 'error', alertLoginStyles());
      setTimeout(() => hideAlert(), 3000);
      return;
    }

    const login = `${crfValue}-${ufValue}`;
    setValue('login', login)

    return verifyPharmaceutical(login);
  }

  const pharmaceuticalInputOnKeyPress = (event: any) => {
    if (event.key === 'Enter') {

      //unfocus input
      event.target.blur();
      // prevent form submit
      event.preventDefault();
      event.stopPropagation();

      handleLoginInput();
    }
  };

  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const inputStyle = {
    ...inputMUIDefaultStyles.large,
    height: isSmallScreen ? '80px' : inputMUIDefaultStyles.large.height,
  };

  return (
    <>
      <Grid container>
        <Grid item lg={6} md={12} className="py-5 my-5 text-center">
          <Container component="main" maxWidth="sm" className="justify-content-center" id='container'>
            <div className="py-5 m-auto">
              <img className="mb-4 w-75" src="/assets/img/logo/logo-abracar-a-vida.png" alt="Programa Abraçar a Vida" />
              <Typography component="h1" variant="h5" className="title text-black">
                Boas-vindas ao Programa
              </Typography>
              <Typography variant="body1" gutterBottom className="sub-title text-grey mt-4 mb-5">
                Preencha os campos abaixo para
                <b> iniciar sua sessão ou cadastrar-se.</b>
              </Typography>
              <form noValidate onSubmit={doSubmit}>
                <InputsBox className="gap-2 pb-4">
                  <TextField
                    variant="outlined"
                    disabled={showLogin}
                    required
                    fullWidth
                    id="crf"
                    autoComplete="crf"
                    label="Farmacêutico"
                    {...register('crf')}
                    helperText="Digite o CRF"
                    style={inputStyle}
                    InputLabelProps={inputLabelProps}
                    inputProps={{ maxLength: 5 }}
                    onKeyPress={pharmaceuticalInputOnKeyPress}
                    onChange={(event: { target: { value: any } }) => {
                      const value = event.target.value;
                      setValue('crf', value);
                      if (showLogin) setShowLogin(false);
                    }}
                  />
                  <SelectInput
                    title="Estado *"
                    disabled={showLogin}
                    list={statesList}
                    style={inputMUIDefaultStyles.large}
                    formData={formData}
                    propertieFormName="uf"
                    onChange={(event: { target: { value: any } }) => {
                      const newValue = event.target.value;
                      setValue('uf', newValue);
                    }}
                  />
                </InputsBox>
                {
                  !showLogin &&
                  <Button
                    className={styles.ButtonLogin}
                    style={{marginTop: '30px'}}
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleLoginInput();
                    }}
                  >
                    Login
                  </Button>
                }
                <LoginInputs
                  formData={formData}
                  showLogin={showLogin}
                  recaptchaRef={recaptchaRef}
                  recaptchaValueSet={recaptchaValueSet}
                />
              </form>
            </div>
          </Container>
        </Grid>
        <Grid item lg={6} className={styles.LoginBg} style={{ backgroundImage: 'linear-gradient(rgba(21, 53, 102, 0.5), rgba(21, 53, 102, 0.5)), url("/assets/img/login-bg.webp")' }}>
          &nbsp;
        </Grid>
      </Grid>
      <Footer />
    </>
  );
};

export default PharmaceuticalLoginPage;
